import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Linkedin, Mail, Send } from "lucide-react";
import Reveal, { EASE } from "./Reveal";

const links = [
  {
    label: "LINKEDIN",
    value: "linkedin.com/in/dmitriy-trapeznikov",
    href: "https://www.linkedin.com/in/dmitriy-trapeznikov/",
    icon: Linkedin,
    testid: "connect-linkedin",
  },
  {
    label: "TELEGRAM",
    value: "t.me/trapez_dm",
    href: "https://t.me/trapez_dm",
    icon: Send,
    testid: "connect-telegram",
  },
  {
    label: "GMAIL",
    value: "trapeznikov.dev@gmail.com",
    href: "mailto:trapeznikov.dev@gmail.com",
    icon: Mail,
    testid: "connect-gmail",
  },
];

export default function Connect() {
  const headlineRef = useRef(null);
  const headlineInView = useInView(headlineRef, { once: true, margin: "-80px" });

  return (
    <section id="connect" data-testid="connect-section" className="relative border-t border-white/5 py-16 lg:py-24">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 55% 45% at 50% 100%, rgba(59,130,246,0.07), transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <div className="font-mono text-xs tracking-[0.35em] text-accent">[ 04 ]</div>
        </Reveal>
        <h2 ref={headlineRef} className="mt-6 flex items-baseline gap-x-4 whitespace-nowrap font-display font-extrabold leading-[0.95] tracking-tight text-white">
          <span className="block overflow-hidden">
            <motion.div
              className="text-[clamp(3rem,9vw,7.5rem)]"
              initial={{ y: "110%" }}
              animate={headlineInView ? { y: 0 } : {}}
              transition={{ duration: 1.1, ease: EASE }}
            >
              Let's
            </motion.div>
          </span>
          <span className="block overflow-hidden">
            <motion.div
              className="text-[clamp(3rem,9vw,7.5rem)] text-zinc-600 transition-colors duration-700 hover:text-accent"
              initial={{ y: "110%" }}
              animate={headlineInView ? { y: 0 } : {}}
              transition={{ duration: 1.1, ease: EASE, delay: 0.12 }}
            >
               connect
            </motion.div>
          </span>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
            Open to new roles, interesting products and good conversations.
            Pick whichever channel you like — I answer fast.
          </p>
        </Reveal>

        <div className="mt-16 border-t border-white/5">
          {links.map((l, i) => (
            <Reveal key={l.label} delay={0.1 + i * 0.08}>
              <a
                data-testid={l.testid}
                href={l.href}
                target={l.href.startsWith("mailto") ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-6 border-b border-white/5 py-7 transition-colors duration-300 hover:bg-white/[0.02]"
              >
                <div className="flex items-center gap-6 sm:gap-10">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-600">
                    0{i + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-3 font-mono text-xs tracking-[0.3em] text-zinc-500">
                      <l.icon className="h-3.5 w-3.5 text-accent" />
                      {l.label}
                    </div>
                    <div className="mt-1.5 font-display text-xl font-semibold text-zinc-300 transition-colors duration-300 group-hover:text-white sm:text-2xl">
                      {l.value}
                    </div>
                  </div>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 transition-all duration-300 group-hover:border-accent group-hover:bg-accent group-hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]">
                  <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
